import React from 'react';

import * as S from './styles';

interface IProps {
  /** The picture. Absent, blank or failing to load all fall back to initials. */
  src?: string | null;
  /** The person's name. Its initials are the fallback. */
  name?: string | null;
  /** `lg` is the design's `.avatar.lg`, for a person card. Default is the 26px one. */
  size?: 'sm' | 'lg';
  className?: string;
}

/**
 * A person's picture, or their initials.
 *
 * The design has two objects for "a small square with a picture in it" and
 * they are not interchangeable:
 *
 *   `.avatar`  a circle, initials underneath  → a **person**
 *   `.thumb`   a rounded square, one initial  → a **thing** (menu item,
 *              category icon, order photo) — see `components/thumb`
 *
 * The division is worth keeping. A circle reads as somebody and a square
 * reads as something, which is most of what these two elements are for at
 * 26px.
 *
 * The fallback matters for the same reason it does on Thumb: profile pictures
 * are imgproxy URLs and most of them 404 in this environment, and a bare
 * `<img>` against a 404 draws the browser's broken-image glyph. Several call
 * sites tried to handle that with `src={picture || '/images/default-avatar.jpg'}`,
 * which never ran — the URL is not absent, it is present and broken.
 *
 * Initials rather than one letter: two people whose names begin with the same
 * letter are common, and `RA` / `TT` tell them apart where `R` / `T` do not.
 */
const initialsOf = (name?: string | null): string => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const Avatar: React.FC<IProps> = ({ src, name, size = 'sm', className }) => {
  const [failed, setFailed] = React.useState(false);

  /* Resets when the element is recycled onto a different person, so one
     broken picture cannot poison the cell for everyone after it. */
  React.useEffect(() => setFailed(false), [src]);

  return (
    <S.Avatar className={[size, className].filter(Boolean).join(' ')} title={name || undefined}>
      {src && !failed ? <S.Image src={src} alt="" onError={() => setFailed(true)} /> : initialsOf(name)}
    </S.Avatar>
  );
};

export default Avatar;
